import React, { useMemo } from "react";
import FilterCard, { FilterRemoveButton } from "./FilterCard";
import { MapPinIcon } from "@heroicons/react/24/outline";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import { Checkbox } from "components/Graphs/Checkbox";
import { GPUInstanceType } from "types/InstanceTypes";
import {
  regionToReadable,
  deriveGeo,
  LocationReadable,
} from "components/utils";

export interface RegionFilterState {
  geo: string | null;
  country: string | null;
  zone: string | null; // city name (zone), not raw location
}

export const NO_REGION_FILTER: RegionFilterState = {
  geo: null,
  country: null,
  zone: null,
};

interface RegionFilterProps {
  regionFilter: RegionFilterState;
  setRegionFilter: (filter: RegionFilterState) => void;
  allInstances: GPUInstanceType[];
}

function getLocationInfo(loc: string): LocationReadable {
  const info = regionToReadable[loc];
  if (info) return info;
  return { city: loc, country: "Unknown", geo: deriveGeo("Unknown") };
}

/** Given a zone (city name), return all raw location strings that map to it. */
export function rawLocationsForZone(zone: string): string[] {
  const locs: string[] = [];
  for (const [raw, info] of Object.entries(regionToReadable)) {
    if (info.city === zone) locs.push(raw);
  }
  return locs;
}

/** Raw locations matching the region filter; null means no filter is active. */
export function rawLocationsMatchingFilter(
  filter: RegionFilterState,
): Set<string> | null {
  if (!filter.geo && !filter.country && !filter.zone) return null;
  const locs = new Set<string>();
  for (const [raw, info] of Object.entries(regionToReadable)) {
    if (filter.geo && info.geo !== filter.geo) continue;
    if (filter.country && info.country !== filter.country) continue;
    if (filter.zone && info.city !== filter.zone) continue;
    locs.add(raw);
  }
  return locs;
}

export function usePickerAllowedLocations(
  regionFilter: RegionFilterState,
  locationName?: string | null,
): Set<string> | null {
  return useMemo(
    () =>
      rawLocationsMatchingFilter(regionFilter) ??
      (locationName ? new Set([locationName]) : null),
    [regionFilter, locationName],
  );
}

interface ZoneEntry {
  city: string;
  country: string;
  geo: string;
  rawLocations: string[];
}

function instanceMatchesRawLocs(
  inst: GPUInstanceType,
  rawLocs: Set<string>,
): boolean {
  if (inst.location && rawLocs.has(inst.location)) return true;
  return inst.available_locations?.some((l) => rawLocs.has(l)) ?? false;
}

const RegionFilter: React.FC<RegionFilterProps> = ({
  regionFilter,
  setRegionFilter,
  allInstances,
}) => {
  // Collect all unique raw locations from instances, skipping unmapped ones
  const allRawLocations = useMemo(() => {
    const locs = new Set<string>();
    for (const inst of allInstances) {
      if (inst.location && regionToReadable[inst.location])
        locs.add(inst.location);
      if (inst.available_locations) {
        for (const l of inst.available_locations) {
          if (regionToReadable[l]) locs.add(l);
        }
      }
    }
    return [...locs];
  }, [allInstances]);

  // Group raw locations into zones (by city) — mirrors cloud-monitor's lat/lng grouping
  const zoneMap = useMemo(() => {
    const map = new Map<string, ZoneEntry>();
    for (const raw of allRawLocations) {
      const info = getLocationInfo(raw);
      const existing = map.get(info.city);
      if (existing) {
        existing.rawLocations.push(raw);
      } else {
        map.set(info.city, {
          city: info.city,
          country: info.country,
          geo: info.geo,
          rawLocations: [raw],
        });
      }
    }
    return map;
  }, [allRawLocations]);

  // Count instances per geo
  const geoInstanceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const inst of allInstances) {
      const locs = inst.available_locations?.length
        ? inst.available_locations
        : inst.location
          ? [inst.location]
          : [];
      const geos = new Set<string>();
      for (const loc of locs) {
        geos.add(getLocationInfo(loc).geo);
      }
      for (const geo of geos) {
        counts.set(geo, (counts.get(geo) || 0) + 1);
      }
    }
    return counts;
  }, [allInstances]);

  // Count instances per country (filtered by geo)
  const countryInstanceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const inst of allInstances) {
      const locs = inst.available_locations?.length
        ? inst.available_locations
        : inst.location
          ? [inst.location]
          : [];
      const countries = new Set<string>();
      for (const loc of locs) {
        const info = getLocationInfo(loc);
        if (regionFilter.geo && info.geo !== regionFilter.geo) continue;
        countries.add(info.country);
      }
      for (const country of countries) {
        counts.set(country, (counts.get(country) || 0) + 1);
      }
    }
    return counts;
  }, [allInstances, regionFilter.geo]);

  // Count instances per zone (filtered by geo + country)
  const zoneInstanceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const zone of zoneMap.values()) {
      if (regionFilter.geo && zone.geo !== regionFilter.geo) continue;
      if (regionFilter.country && zone.country !== regionFilter.country)
        continue;
      const rawSet = new Set(zone.rawLocations);
      let count = 0;
      for (const inst of allInstances) {
        if (instanceMatchesRawLocs(inst, rawSet)) count++;
      }
      counts.set(zone.city, count);
    }
    return counts;
  }, [allInstances, zoneMap, regionFilter.geo, regionFilter.country]);

  // Geo list
  const geos = useMemo(() => {
    const seen = new Set<string>();
    for (const zone of zoneMap.values()) seen.add(zone.geo);
    return [...seen]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ name, count: geoInstanceCounts.get(name) || 0 }));
  }, [zoneMap, geoInstanceCounts]);

  // Country list — filtered by geo
  const countries = useMemo(() => {
    const seen = new Set<string>();
    for (const zone of zoneMap.values()) {
      if (regionFilter.geo && zone.geo !== regionFilter.geo) continue;
      seen.add(zone.country);
    }
    return [...seen]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ name, count: countryInstanceCounts.get(name) || 0 }));
  }, [zoneMap, regionFilter.geo, countryInstanceCounts]);

  // Zone list — filtered by geo and country
  const zones = useMemo(() => {
    const list: ZoneEntry[] = [];
    for (const zone of zoneMap.values()) {
      if (regionFilter.geo && zone.geo !== regionFilter.geo) continue;
      if (regionFilter.country && zone.country !== regionFilter.country)
        continue;
      list.push(zone);
    }
    return list.sort((a, b) => a.city.localeCompare(b.city));
  }, [zoneMap, regionFilter.geo, regionFilter.country]);

  const isActive =
    regionFilter.geo !== null ||
    regionFilter.country !== null ||
    regionFilter.zone !== null;

  const label = regionFilter.zone
    ? regionFilter.zone
    : regionFilter.country
      ? regionFilter.country
      : regionFilter.geo
        ? regionFilter.geo
        : "All regions";

  return (
    <Popover>
      <div className="relative inline-flex">
        <PopoverTrigger asChild>
          <FilterCard
            icon={<MapPinIcon className="w-4 h-4" />}
            title={label}
            filterApplied={isActive}
            hasTrailingAction={isActive}
          />
        </PopoverTrigger>
        {isActive && (
          <FilterRemoveButton
            filterName="region"
            onRemove={() =>
              setRegionFilter({ geo: null, country: null, zone: null })
            }
          />
        )}
      </div>
      <PopoverContent className="w-[calc(100vw-2rem)] max-w-[540px] overflow-y-auto p-4">
        <div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-6">
          {/* Geo column */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 px-1 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                Geo
              </span>
              <div className="h-px flex-1 bg-zinc-800" />
            </div>
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/50 cursor-pointer">
                <Checkbox
                  checked={regionFilter.geo === null}
                  onCheckedChange={() =>
                    setRegionFilter({ geo: null, country: null, zone: null })
                  }
                />
                <span className="text-xs text-white">Any</span>
                <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-800 text-zinc-400">
                  {allInstances.length}
                </span>
              </label>
              {geos.map((g) => (
                <label
                  key={g.name}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/50 cursor-pointer"
                >
                  <Checkbox
                    checked={regionFilter.geo === g.name}
                    onCheckedChange={() =>
                      setRegionFilter({
                        geo: regionFilter.geo === g.name ? null : g.name,
                        country: null,
                        zone: null,
                      })
                    }
                  />
                  <span className="text-xs text-white">{g.name}</span>
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-800 text-zinc-400">
                    {g.count}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Country column */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 px-1 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                Country
              </span>
              <div className="h-px flex-1 bg-zinc-800" />
            </div>
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/50 cursor-pointer">
                <Checkbox
                  checked={regionFilter.country === null}
                  onCheckedChange={() =>
                    setRegionFilter({
                      ...regionFilter,
                      country: null,
                      zone: null,
                    })
                  }
                />
                <span className="text-xs text-white">Any</span>
                <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-800 text-zinc-400">
                  {regionFilter.geo
                    ? geoInstanceCounts.get(regionFilter.geo) || 0
                    : allInstances.length}
                </span>
              </label>
              {countries.map((c) => (
                <label
                  key={c.name}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/50 cursor-pointer"
                >
                  <Checkbox
                    checked={regionFilter.country === c.name}
                    onCheckedChange={() =>
                      setRegionFilter({
                        ...regionFilter,
                        country:
                          regionFilter.country === c.name ? null : c.name,
                        zone: null,
                      })
                    }
                  />
                  <span className="text-xs text-white">{c.name}</span>
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-800 text-zinc-400">
                    {c.count}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Zone column */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 px-1 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                Zone
              </span>
              <div className="h-px flex-1 bg-zinc-800" />
            </div>
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/50 cursor-pointer">
                <Checkbox
                  checked={regionFilter.zone === null}
                  onCheckedChange={() =>
                    setRegionFilter({ ...regionFilter, zone: null })
                  }
                />
                <span className="text-xs text-white">Any</span>
                <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-800 text-zinc-400">
                  {regionFilter.country
                    ? countryInstanceCounts.get(regionFilter.country) || 0
                    : regionFilter.geo
                      ? geoInstanceCounts.get(regionFilter.geo) || 0
                      : allInstances.length}
                </span>
              </label>
              {zones.map((z) => (
                <label
                  key={z.city}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-800/50 cursor-pointer"
                >
                  <Checkbox
                    checked={regionFilter.zone === z.city}
                    onCheckedChange={() =>
                      setRegionFilter({
                        ...regionFilter,
                        zone: regionFilter.zone === z.city ? null : z.city,
                      })
                    }
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs text-white truncate">
                      {z.city}
                    </span>
                    <span className="text-[10px] text-zinc-500 font-mono truncate">
                      {z.rawLocations.length === 1
                        ? z.rawLocations[0]
                        : `${z.rawLocations.length} locations`}
                    </span>
                  </div>
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-800 text-zinc-400">
                    {zoneInstanceCounts.get(z.city) || 0}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default RegionFilter;
